import { useEffect, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useQueryClient } from 'react-query';

import { DMM_KEIRIN_YOUTUBE_CH_ID } from '@/config/constants/youtube';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import { LiveVideoInfo } from '@/lib/microcms/client/types/model';
import { DmmLiveOption } from '@/lib/microcms/model';
import { LiveSiteName } from '@/lib/race';
import { Velodrome } from '@/modelTypes';
import { liveVideoStorage } from '@/storages/liveVideo';

type LiveLink = {
  liveSite: LiveSiteName;
  url: string;
};
interface State {
  liveVideoInfo: LiveVideoInfo;
  dmmLiveOption: DmmLiveOption | null;
  liveLink: LiveLink;
  liveUrl: string;
  isReplaced: boolean; // ライブ動画リンクをDMM競輪チャンネルに切り替えるか否か
  muted: boolean;
  setMuted: React.Dispatch<React.SetStateAction<boolean>>;
}

const initLiveVideoInfo: LiveVideoInfo = {
  youtubeChannelId: '',
  embeddableYoutube: false,
  niconicoChannelId: '',
  officialUrl: '',
};

const useLiveVideo = (velodrome: Velodrome): State => {
  const queryClient = useQueryClient();

  const [liveVideoInfo, setLiveVideoInfo] =
    useState<LiveVideoInfo>(initLiveVideoInfo);
  const [dmmLiveOption, setDmmLiveOption] = useState<DmmLiveOption | null>(
    null,
  );
  const [muted, setMuted] = useState<boolean>(liveVideoStorage.get() === null);

  useEffect(() => {
    liveVideoStorage.set(muted);
  }, [muted]);

  const isReplacedToDmmKeirinCh = useMemo(() => {
    return dmmLiveOption !== null;
  }, [dmmLiveOption, velodrome]);

  const liveLink = useMemo((): LiveLink => {
    if (isReplacedToDmmKeirinCh) {
      return {
        liveSite: LiveSiteName.YouTube,
        url: `https://www.youtube.com/channel/${DMM_KEIRIN_YOUTUBE_CH_ID}/live`,
      };
    }
    if (liveVideoInfo.youtubeChannelId) {
      return {
        liveSite: LiveSiteName.YouTube,
        url: `https://www.youtube.com/channel/${liveVideoInfo.youtubeChannelId}/live`,
      };
    }
    if (liveVideoInfo.niconicoChannelId) {
      return {
        liveSite: LiveSiteName.NicoNama,
        url: `https://ch.nicovideo.jp/${liveVideoInfo.niconicoChannelId}/live`,
      };
    }
    if (liveVideoInfo.officialUrl) {
      return {
        liveSite: LiveSiteName.Official,
        url: liveVideoInfo.officialUrl,
      };
    }
    return {
      liveSite: LiveSiteName.KeirinJP,
      url: `http://keirin.jp${isMobileOnly ? '/sp/top#ctabToday' : '/pc/top'}`,
    };
  }, [isReplacedToDmmKeirinCh, liveVideoInfo, velodrome]);

  const liveUrl = useMemo(() => {
    if (isReplacedToDmmKeirinCh && dmmLiveOption!.videoId) {
      return `https://www.youtube.com/embed/${dmmLiveOption!.videoId}`;
    }
    return `https://www.youtube.com/embed/live_stream?channel=${
      isReplacedToDmmKeirinCh
        ? DMM_KEIRIN_YOUTUBE_CH_ID
        : liveVideoInfo.youtubeChannelId
    }&enablejsapi=1`;
  }, [dmmLiveOption, liveVideoInfo, velodrome]);

  useEffect(() => {
    queryClient
      .fetchQuery(['liveVideoInfo', velodrome.romanName], async () =>
        client.getContent('velodrome', velodrome.romanName, {
          fields: 'liveVideoInfo',
        }),
      )
      .then(res => setLiveVideoInfo(res.liveVideoInfo || initLiveVideoInfo))
      .catch(() => setLiveVideoInfo(initLiveVideoInfo));

    queryClient
      .fetchQuery('dmmlive', async () =>
        client.getContents('dmmlive', {
          fields: 'velodrome.velodromeName,startTime,endTime,videoId',
        }),
      )
      .then(res => {
        const targetOption = res.find(
          opt =>
            opt.velodrome.velodromeName === velodrome.name &&
            dayjst().isBetween(opt.startTime, opt.endTime),
        );
        setDmmLiveOption(targetOption || null);
      })
      .catch(() => setDmmLiveOption(null));
  }, [velodrome.romanName]);

  return {
    liveVideoInfo,
    dmmLiveOption,
    liveLink,
    liveUrl,
    isReplaced: isReplacedToDmmKeirinCh,
    muted,
    setMuted,
  };
};

export default useLiveVideo;
